import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

type SkeletonWidth = "full" | "1/4" | "1/3" | "1/2" | "2/3" | "16" | "32" | "40";
// No `auto`: the skeleton is an empty span, so an auto height renders nothing.
type SkeletonHeight = "2" | "4" | "5" | "6" | "8" | "10" | "16" | "20" | "28" | "40" | "full";
type SkeletonShape = "bar" | "roundedTop";

const WIDTH: Record<SkeletonWidth, string> = {
  full: "w-full",
  "1/4": "w-skeleton-quarter",
  "1/3": "w-skeleton-third",
  "1/2": "w-skeleton-half",
  "2/3": "w-skeleton-two-thirds",
  "16": "w-skeleton-narrow",
  "32": "w-skeleton-medium",
  "40": "w-skeleton-wide",
};
const HEIGHT: Record<SkeletonHeight, string> = {
  "2": "h-skeleton-2xs",
  "4": "h-skeleton-xs",
  "5": "h-skeleton-sm",
  "6": "h-skeleton-md",
  "8": "h-skeleton-lg",
  "10": "h-skeleton-xl",
  "16": "h-skeleton-2xl",
  "20": "h-skeleton-3xl",
  "28": "h-skeleton-4xl",
  "40": "h-skeleton-5xl",
  full: "h-full",
};
const SHAPE: Record<SkeletonShape, string> = { bar: "rounded-xs", roundedTop: "rounded-t-full" };

interface SkeletonProps {
  width?: SkeletonWidth;
  height?: SkeletonHeight;
  shape?: SkeletonShape;
}

/**
 * Pulsing placeholder for content that is still loading. Renders an empty span, so
 * it needs both a `width` and a `height` to take up room: it defaults to the full
 * width of its parent and a single line of text (`height="6"`).
 *
 * `shape` picks the corner treatment: `bar` for text and blocks, `roundedTop` for
 * placeholders that stand in for a chart column.
 */
export function Skeleton({ width = "full", height = "6", shape = "bar" }: SkeletonProps): ReactNode {
  return (
    <span
      className={twJoin(
        "block shrink-0 animate-pulse bg-background-secondary",
        WIDTH[width],
        HEIGHT[height],
        SHAPE[shape],
      )}
    />
  );
}
